import { z } from 'zod/v3';

// ========== Asset Inventory DTOs ==========
//
// Cross-framework asset register. Same model serves SOC 2 CC3.2, HIPAA
// §164.308(a)(1)(ii)(B), ISO 27001 8.1, PCI DSS 9.9, NIST CM-8.
// HIPAA/PCI-specific fields (containsEphi, containsPci) render in the
// UI conditionally on workspace.enabledFrameworks.

export const InventoryAssetTypeEnum = z.enum([
  'COMPUTE',
  'STORAGE',
  'DATABASE',
  'NETWORK',
  'IDENTITY',
  'SAAS',
  'CONTAINER',
  'REPOSITORY',
  'DEVICE',
  'FACILITY',
  'DATASET',
  'APPLICATION',
  /** RFC-216 M1 — an AI system; its components hang off it via parentAsset. */
  'AI_SYSTEM',
  /** RFC-216 — components of an AI system. Each requires an `AI_SYSTEM` parent.
   *  No AI-specific dataset member: a training corpus reuses `DATASET` above. */
  'MODEL',
  'MCP_SERVER',
  'PROMPT',
  'AGENT',
  'TOOL',
  'GUARDRAIL',
  'OTHER',
]);
export type InventoryAssetType = z.infer<typeof InventoryAssetTypeEnum>;

/**
 * RFC-216 M1 — subcategory for the components of an AI system. Set on CHILD
 * rows under an `AI_SYSTEM` parent, never on the parent itself.
 */
/**
 * Register buckets (Eben working session 2026-07-16): the asset table
 * stays ONE audit-canonical inventory; these are presentation groupings.
 * Devices = physical; Software = licensed applications; Cloud = the
 * infrastructure running the product.
 */
export const ASSET_CATEGORIES = ['devices', 'software', 'ai_system', 'cloud', 'other'] as const;
export type AssetCategory = (typeof ASSET_CATEGORIES)[number];

export const ASSET_CATEGORY_FOR_TYPE: Record<InventoryAssetType, AssetCategory> = {
  DEVICE: 'devices',
  FACILITY: 'devices',
  SAAS: 'software',
  APPLICATION: 'software',
  // RFC-216 — the category IS the AI system, not a flat "AI" bucket. The other
  // categories are presentation groupings with no corresponding type; this one
  // has a ROOT type, and its other members are what an AI system is composed
  // of. Listing a Model as a peer of AI_SYSTEM said they were the same kind of
  // thing differently named — they are container and part.
  //
  // AI_SYSTEM stays mapped here so the type space still partitions cleanly; the
  // container/part distinction is expressed by the picker, which offers the
  // components only under an AI_SYSTEM parent.
  AI_SYSTEM: 'ai_system',
  MODEL: 'ai_system',
  MCP_SERVER: 'ai_system',
  PROMPT: 'ai_system',
  AGENT: 'ai_system',
  TOOL: 'ai_system',
  GUARDRAIL: 'ai_system',
  COMPUTE: 'cloud',
  STORAGE: 'cloud',
  DATABASE: 'cloud',
  NETWORK: 'cloud',
  IDENTITY: 'cloud',
  CONTAINER: 'cloud',
  REPOSITORY: 'cloud',
  DATASET: 'cloud',
  OTHER: 'other',
};

export function assetTypesForCategory(category: AssetCategory): InventoryAssetType[] {
  return (Object.keys(ASSET_CATEGORY_FOR_TYPE) as InventoryAssetType[]).filter(
    (t) => ASSET_CATEGORY_FOR_TYPE[t] === category,
  );
}

export const AssetCriticalityEnum = z.enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']);
export type AssetCriticality = z.infer<typeof AssetCriticalityEnum>;

export const DataClassificationEnum = z.enum([
  'PUBLIC',
  'INTERNAL',
  'CONFIDENTIAL',
  'RESTRICTED',
]);
export type DataClassification = z.infer<typeof DataClassificationEnum>;

export const AssetStatusEnum = z.enum([
  'ACTIVE',
  'INACTIVE',
  'DECOMMISSIONED',
  'UNKNOWN',
]);
export type AssetStatus = z.infer<typeof AssetStatusEnum>;

export const AssetSyncStatusEnum = z.enum([
  'SYNCED',
  'DRIFTED',
  'ORPHANED',
  'ERROR',
]);
export type AssetSyncStatus = z.infer<typeof AssetSyncStatusEnum>;

export const AiDeploymentStageEnum = z.enum([
  'EXPLORATORY',
  'DEVELOPMENT',
  'PILOT',
  'PRODUCTION',
  'RETIRED',
]);
export type AiDeploymentStage = z.infer<typeof AiDeploymentStageEnum>;

/**
 * RFC-216 follow-up — the purpose half of an `AI_SYSTEM` asset, stored 1:1 as
 * `AiSystemProfile` (the same spine+extension shape `EmployeeDevice` uses for
 * `DEVICE`). Present only on AI systems; null for every other asset type and
 * for AI *components* (a prompt has no affected population of its own — its
 * parent system does).
 */
export const AiSystemProfileDTOSchema = z.object({
  intendedPurpose: z.string().nullable(),
  outOfScopeUses: z.string().nullable(),
  affectedPopulations: z.array(z.string()),
  deploymentStage: AiDeploymentStageEnum,
  businessOwnerId: z.string().nullable(),
  businessOwnerName: z.string().nullable(),
});
export type AiSystemProfileDTO = z.infer<typeof AiSystemProfileDTOSchema>;

/**
 * Write shape. Every field optional so a caller can set intended purpose
 * without restating the deployment stage; omitted fields keep their stored
 * value. Supplying this on create/update upserts the profile.
 */
export const AiSystemProfileInputSchema = z.object({
  intendedPurpose: z.string().nullable().optional(),
  outOfScopeUses: z.string().nullable().optional(),
  affectedPopulations: z.array(z.string()).optional(),
  deploymentStage: AiDeploymentStageEnum.optional(),
  businessOwnerId: z.string().nullable().optional(),
});
export type AiSystemProfileInput = z.infer<typeof AiSystemProfileInputSchema>;

/**
 * #1423 M2: pending classification suggestion produced by the inference
 * pass (heuristics + LLM). Stored on Asset.classificationSuggestion as
 * JSONB; never takes effect until a human accepts it.
 */
export const AssetClassificationSuggestionSchema = z.object({
  /**
   * OUT_OF_SCOPE — resource looks non-prod / outside audit scope; accept
   *   creates SCOPE wildcard exceptions (RFC-182).
   * NO_SENSITIVE_DATA — in scope, but holds no customer data; accept
   *   writes the classification and waives data-protection checks.
   * SENSITIVE_DATA — holds customer data; accept writes classification
   *   (containsPii/Pci/Ephi + dataClassification). No exceptions.
   */
  verdict: z.enum(['OUT_OF_SCOPE', 'NO_SENSITIVE_DATA', 'SENSITIVE_DATA']),
  /** Detected environment bucket for OUT_OF_SCOPE (e.g. "development"). */
  environment: z.string().nullish(),
  dataClassification: DataClassificationEnum.nullish(),
  containsPii: z.boolean().nullish(),
  containsPci: z.boolean().nullish(),
  containsEphi: z.boolean().nullish(),
  containsEmployeeData: z.boolean().nullish(),
  containsIntellectualProperty: z.boolean().nullish(),
  /** 0–1. Heuristic verdicts are 0.7–0.95; LLM verdicts carry its own score. */
  confidence: z.number(),
  /** One-line, auditor-readable evidence for the verdict. */
  rationale: z.string(),
  source: z.enum(['heuristic', 'llm']),
  suggestedAt: z.string(),
  /** Set when the user dismisses — re-runs must not re-suggest. */
  dismissedAt: z.string().nullish(),
});
export type AssetClassificationSuggestion = z.infer<
  typeof AssetClassificationSuggestionSchema
>;

/**
 * Canonical mapping between the per-asset Data Scope flags and the
 * onboarding "what data do you handle?" declarations persisted to
 * `ComplianceProgram.dataTypes`. The two vocabularies are deliberately
 * different shapes (business-level declarations vs. per-resource legal
 * flags) — this table is the single place their relationship lives.
 * Consumers: the data-scope-drift gap detector, classification
 * inference gating.
 */
export const PROGRAM_DATA_TYPES_FOR_ASSET_FLAG = {
  // IP requires the additional customer-ownership confirmation before scope comparison.
  containsPii: ['CUSTOMER_PII'],
  containsPci: ['PAYMENT'],
  containsEphi: ['HEALTH'],
  containsEmployeeData: ['EMPLOYEE'],
  containsIntellectualProperty: ['CUSTOMER_IP'],
} as const;
export type AssetDataScopeFlag = keyof typeof PROGRAM_DATA_TYPES_FOR_ASSET_FLAG;

export const AssetDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  sequenceNumber: z.number(),
  displayId: z.string(), // "AST-001"

  // Core
  name: z.string(),
  assetType: InventoryAssetTypeEnum,
  description: z.string().nullable(),

  // Classification
  criticality: AssetCriticalityEnum,
  dataClassification: DataClassificationEnum,
  dataTypes: z.array(z.string()),

  // Framework-conditional flags
  containsEphi: z.boolean(),
  containsPii: z.boolean(),
  containsPci: z.boolean(),
  containsIntellectualProperty: z.boolean(),
  containsCustomerIntellectualProperty: z.boolean().nullable(),
  containsEmployeeData: z.boolean(),

  // Location / provider
  provider: z.string(),
  providerAssetId: z.string().nullable(),
  providerAccount: z.string().nullable(),
  region: z.string().nullable(),
  environment: z.string().nullable(),
  tags: z.record(z.unknown()).nullable(),

  // Ownership
  ownerId: z.string().nullable(),
  ownerLabel: z.string(),
  ownerRoleName: z.string().nullable(),
  technicalOwnerId: z.string().nullable(),
  technicalOwnerName: z.string().nullable(),

  // Third-party relationship
  vendorId: z.string().nullable(),
  vendorName: z.string().nullable(),

  // Lifecycle
  status: AssetStatusEnum,
  discoveredAt: z.coerce.date(),
  lastReviewedAt: z.coerce.date().nullable(),
  decommissionedAt: z.coerce.date().nullable(),

  // Sync provenance
  syncStatus: AssetSyncStatusEnum.nullable(),
  lastSyncedAt: z.coerce.date().nullable(),

  // Hierarchy
  parentAssetId: z.string().nullable(),

  /** RFC-216 follow-up — purpose/affected-population extension. Non-null only
   *  for `AI_SYSTEM` rows, and only once someone has filled it in. */
  aiProfile: AiSystemProfileDTOSchema.nullish(),

  /** RFC-216 M1 — set on children of an `AI_SYSTEM` parent; null otherwise. */

  // #1423 M2: pending suggestion awaiting review (null = none)
  classificationSuggestion: AssetClassificationSuggestionSchema.nullable(),

  /** #1423: an active SCOPE exception matches one of this asset's id forms.
   *  Annotated by the list endpoint; absent elsewhere. */
  outOfScope: z.boolean().nullish(),

  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AssetDTO = z.infer<typeof AssetDTOSchema>;

export const CreateAssetDTOSchema = z.object({
  name: z.string().min(1).max(200),
  assetType: InventoryAssetTypeEnum,
  description: z.string().nullable().optional(),
  criticality: AssetCriticalityEnum.optional(),
  dataClassification: DataClassificationEnum.optional(),
  dataTypes: z.array(z.string()).optional(),

  containsEphi: z.boolean().optional(),
  containsPii: z.boolean().optional(),
  containsPci: z.boolean().optional(),
  containsIntellectualProperty: z.boolean().optional(),
  containsCustomerIntellectualProperty: z.boolean().nullable().optional(),
  containsEmployeeData: z.boolean().optional(),

  provider: z.string().optional(),
  providerAssetId: z.string().nullable().optional(),
  providerAccount: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  environment: z.string().nullable().optional(),
  tags: z.record(z.unknown()).nullable().optional(),

  ownerId: z.string().nullable().optional(),
  technicalOwnerId: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  parentAssetId: z.string().nullable().optional(),
  /** RFC-216 M1 — only valid on a child of an `AI_SYSTEM` parent. */

  status: AssetStatusEnum.optional(),
  notes: z.string().nullable().optional(),

  /** RFC-216 follow-up — rejected unless `assetType` is `AI_SYSTEM`. */
  aiProfile: AiSystemProfileInputSchema.optional(),
});
export type CreateAssetDTO = z.infer<typeof CreateAssetDTOSchema>;

export const UpdateAssetDTOSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  assetType: InventoryAssetTypeEnum.optional(),
  description: z.string().nullable().optional(),
  criticality: AssetCriticalityEnum.optional(),
  dataClassification: DataClassificationEnum.optional(),
  dataTypes: z.array(z.string()).optional(),

  containsEphi: z.boolean().optional(),
  containsPii: z.boolean().optional(),
  containsPci: z.boolean().optional(),
  containsIntellectualProperty: z.boolean().optional(),
  containsCustomerIntellectualProperty: z.boolean().nullable().optional(),
  containsEmployeeData: z.boolean().optional(),

  provider: z.string().optional(),
  providerAssetId: z.string().nullable().optional(),
  providerAccount: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  environment: z.string().nullable().optional(),
  tags: z.record(z.unknown()).nullable().optional(),

  ownerId: z.string().nullable().optional(),
  ownerLabel: z.string().optional(),
  technicalOwnerId: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  parentAssetId: z.string().nullable().optional(),
  /** RFC-216 M1 — only valid on a child of an `AI_SYSTEM` parent. */

  status: AssetStatusEnum.optional(),
  lastReviewedAt: z.coerce.date().nullable().optional(),
  decommissionedAt: z.coerce.date().nullable().optional(),
  notes: z.string().nullable().optional(),

  /** RFC-216 follow-up — upserted, so a partial profile edit does not need the
   *  whole object. Rejected unless the row is (or is becoming) an `AI_SYSTEM`. */
  aiProfile: AiSystemProfileInputSchema.optional(),
});
export type UpdateAssetDTO = z.infer<typeof UpdateAssetDTOSchema>;

// ── RFC-231 M1 / D6 — register an AI system in one form ──────────────────
//
// Most of an AI system's record is human-entered (purpose, populations,
// stage, owner) and its components are what the risk catalog reads, so the
// registration form captures system + profile + components together and the
// server writes them atomically. Components are the AI component types plus
// DATASET: a training or retrieval corpus is a logical data collection that
// takes its AI-ness from the parent.

export const AI_SYSTEM_COMPONENT_TYPES = [
  'MODEL',
  'MCP_SERVER',
  'PROMPT',
  'AGENT',
  'TOOL',
  'GUARDRAIL',
  'DATASET',
] as const;
export const AiSystemComponentTypeEnum = z.enum(AI_SYSTEM_COMPONENT_TYPES);
export type AiSystemComponentType = z.infer<typeof AiSystemComponentTypeEnum>;

export const AiSystemComponentInputSchema = z.object({
  assetType: AiSystemComponentTypeEnum,
  name: z.string().min(1).max(200),
  vendorId: z.string().nullable().optional(),
  provider: z.string().optional(),
  /** "Reaches customer data" in the form. Stored as the register's own PII
   *  flag so the R2 reach rule (RFC-231 §4.4) reads one vocabulary. */
  containsPii: z.boolean().optional(),
  containsEphi: z.boolean().optional(),
  notes: z.string().nullable().optional(),
});
export type AiSystemComponentInput = z.infer<typeof AiSystemComponentInputSchema>;

export const CreateAiSystemDTOSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  criticality: AssetCriticalityEnum.optional(),
  dataClassification: DataClassificationEnum.optional(),
  containsEphi: z.boolean().optional(),
  containsPii: z.boolean().optional(),
  containsPci: z.boolean().optional(),
  containsIntellectualProperty: z.boolean().optional(),
  containsCustomerIntellectualProperty: z.boolean().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  technicalOwnerId: z.string().nullable().optional(),
  vendorId: z.string().nullable().optional(),
  provider: z.string().optional(),
  /** RFC-231 M2 — set when registering from a discovery candidate, so the
   *  system links back to the observed subject and the candidate retires. */
  providerAssetId: z.string().nullable().optional(),
  providerAccount: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  aiProfile: AiSystemProfileInputSchema.optional(),
  components: z.array(AiSystemComponentInputSchema).max(50).default([]),
});
export type CreateAiSystemDTO = z.infer<typeof CreateAiSystemDTOSchema>;

// ── RFC-231 M2 — AI-system candidates (computed live, never stored) ────────
export const AiDiscoverySourceEnum = z.enum(['onboarding', 'idp', 'cloud', 'dependabot', 'manifest']);
export type AiDiscoverySource = z.infer<typeof AiDiscoverySourceEnum>;

export const AiSystemCandidateDTOSchema = z.object({
  key: z.string(),
  suggestedName: z.string(),
  providerName: z.string().nullable(),
  providerAssetId: z.string().nullable(),
  providerAccount: z.string().nullable(),
  signals: z.array(z.object({ source: AiDiscoverySourceEnum, detail: z.string() })),
  components: z.array(z.object({ assetType: AiSystemComponentTypeEnum, name: z.string() })),
  assignedUserCount: z.number().nullable(),
  /** Echo back on dismiss; a dismissal only holds while this is unchanged. */
  signalFingerprint: z.string(),
});
export type AiSystemCandidateDTO = z.infer<typeof AiSystemCandidateDTOSchema>;

export const DismissAiSystemCandidateDTOSchema = z.object({
  candidateKey: z.string().min(1),
  signalFingerprint: z.string(),
});

// ── RFC-231 M3 — catalog-derived risk suggestions (computed live) ──────────
export const PropagationRuleEnum = z.enum(['R1', 'R2']);
export const AiRiskSuggestionDTOSchema = z.object({
  id: z.string(),
  rule: PropagationRuleEnum,
  catalogKey: z.string(),
  systemAssetId: z.string(),
  systemName: z.string(),
  componentAssetId: z.string(),
  componentName: z.string(),
  componentType: AiSystemComponentTypeEnum,
  title: z.string(),
  statement: z.string(),
  category: z.string(),
  likelihood: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  impact: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  mitigation: z.string(),
  /** `AIGovernanceControl.fullId`s, e.g. "OWASP_LLM:LLM01". */
  citations: z.array(z.string()),
  rationale: z.string(),
  fingerprint: z.string(),
});
export type AiRiskSuggestionDTO = z.infer<typeof AiRiskSuggestionDTOSchema>;

export const AcceptAiRiskSuggestionDTOSchema = z.object({ suggestionId: z.string().min(1) });
export const DismissAiRiskSuggestionDTOSchema = z.object({
  suggestionId: z.string().min(1),
  fingerprint: z.string(),
  /** Why the risk does not apply — the audit-visible half of a "not applicable" decision. */
  reason: z.string().trim().min(1).max(500),
});
export const RestoreAiRiskSuggestionDTOSchema = z.object({ suggestionId: z.string().min(1) });

/** A suggestion judged not applicable at its current fingerprint, with the recorded decision. */
export const DismissedAiRiskSuggestionDTOSchema = AiRiskSuggestionDTOSchema.extend({
  reason: z.string().nullable(),
  dismissedByName: z.string().nullable(),
  dismissedAt: z.coerce.date(),
});
export type DismissedAiRiskSuggestionDTO = z.infer<typeof DismissedAiRiskSuggestionDTOSchema>;

/** RFC-231 M1 — inputs for the "you declared AI providers but registered no
 *  AI system" prompt. Read-only; the prompt itself is a client decision. */
export const AiRegistrationStatusDTOSchema = z.object({
  declaredProviders: z.array(z.string()),
  aiSystemCount: z.number(),
});
export type AiRegistrationStatusDTO = z.infer<typeof AiRegistrationStatusDTOSchema>;

export const AssetListFilterSchema = z.object({
  /** Case-insensitive match on name or provider asset id (asset pickers). */
  search: z.string().trim().max(200).optional(),
  assetType: InventoryAssetTypeEnum.optional(),
  /** RFC-216 — the components of one AI system. */
  parentAssetId: z.string().optional(),
  criticality: AssetCriticalityEnum.optional(),
  dataClassification: DataClassificationEnum.optional(),
  status: AssetStatusEnum.optional(),
  provider: z.string().optional(),
  environment: z.string().optional(),
  vendorId: z.string().optional(),
  // Framework-specific filters
  containsEphi: z.boolean().optional(),
  containsPii: z.boolean().optional(),
  containsPci: z.boolean().optional(),
  // Lifecycle
  includeDecommissioned: z.boolean().optional(),
  /** #1423: filter by resource-scope state (derived, not a DB column). */
  scope: z.enum(['in_scope', 'out_of_scope']).optional(),
  /** Register bucket (expands to an assetType IN (...) filter server-side). */
  // Derived from ASSET_CATEGORIES, not a second copy of it. The hardcoded list
  // here silently went stale when RFC-216 added the `ai` bucket, so the Assets
  // page could offer a filter the API rejected with a 400.
  category: z.enum(ASSET_CATEGORIES).optional(),
  // Pagination
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
});

export const AssetStatsDTOSchema = z.object({
  total: z.number(),
  critical: z.number(),
  unclassified: z.number(),
  needsReview: z.number(),
  // HIPAA / PCI slices — always returned, callers render conditionally
  ephiCount: z.number(),
  piiCount: z.number(),
  pciCount: z.number(),
});
export type AssetStatsDTO = z.infer<typeof AssetStatsDTOSchema>;

/** Software license assignment (employee seat on a SAAS/APPLICATION asset). */
export const LicenseAssignmentDTOSchema = z.object({
  id: z.string(),
  assetId: z.string(),
  employeeId: z.string(),
  employeeName: z.string(),
  licenseLabel: z.string().nullable(),
  grantedAt: z.coerce.date(),
  revokedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
});
export type LicenseAssignmentDTO = z.infer<typeof LicenseAssignmentDTOSchema>;

/** Employee-side license view ("Bob has licenses to these 5 apps"). */
export const EmployeeLicenseDTOSchema = z.object({
  id: z.string(),
  assetId: z.string(),
  assetName: z.string(),
  assetType: InventoryAssetTypeEnum,
  licenseLabel: z.string().nullable(),
  grantedAt: z.coerce.date(),
  revokedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
});
export type EmployeeLicenseDTO = z.infer<typeof EmployeeLicenseDTOSchema>;

/** User declarations, not cloud configuration or automatic compliance decisions. */
export const AccountEnvironmentInputSchema = z.object({
  provider: z.enum(['aws', 'azure', 'gcp']),
  account: z.string().trim().min(1).max(256),
  environment: z.enum(['production', 'non-production']).nullable(),
});
export type AccountEnvironmentInput = z.infer<typeof AccountEnvironmentInputSchema>;

export const AccountEnvironmentDeclarationSchema = AccountEnvironmentInputSchema.extend({
  updatedAt: z.string().datetime(),
  updatedBy: z.string(),
});
export type AccountEnvironmentDeclaration = z.infer<typeof AccountEnvironmentDeclarationSchema>;
export interface AssetEnvironmentAccountDTO {
  provider: AccountEnvironmentInput['provider'];
  account: string;
  label: string;
  declaration: AccountEnvironmentDeclaration | null;
}

/** Exact, language-independent tag pairs. Preserve original spelling and whitespace. */
export const EnvironmentTagMappingSchema = z.object({
  key: z.string().min(1),
  value: z.string(),
  environment: z.enum(['production', 'non-production']),
});
export type EnvironmentTagMapping = z.infer<typeof EnvironmentTagMappingSchema>;
export type EnvironmentTagOption = Pick<EnvironmentTagMapping, 'key' | 'value'>;
export const EnvironmentTagSettingsSchema = z.object({
  environmentTagMappings: z.array(EnvironmentTagMappingSchema).max(200).default([]).superRefine((mappings, ctx) => {
    const pairs = new Set<string>();
    mappings.forEach((mapping, index) => {
      const pair = JSON.stringify([mapping.key, mapping.value]);
      if (pairs.has(pair)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index], message: 'Each tag key/value pair can have only one environment mapping.' });
      pairs.add(pair);
    });
  }),
});
export type EnvironmentTagSettings = z.infer<typeof EnvironmentTagSettingsSchema>;
